import React, { useEffect, useRef } from "react";
import LoadingState from "../../Components/LoadingState";
import { Brain, Check, User } from "lucide-react";

/**
 * The training transcript. Three kinds of entry:
 *   user      — what the owner typed
 *   assistant — the assistant's reply
 *   learned    — a `fact_learned` stage event, shown as a callout rather than
 *                a chat bubble, because it's the point of the whole exercise
 */
export default function TrainingThread({
  entries,
  thinking,
  loading = false,
  connected,
  assistantName,
  avatarUrl,
}) {
  const threadRef = useRef(null);

  // The assistant's own avatar when it has one, so training looks like the
  // same assistant the tester and the live widget show.
  const assistantGlyph = avatarUrl ? (
    <img src={avatarUrl} alt="" />
  ) : (
    <Brain size={15} strokeWidth={2} />
  );

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [entries, thinking]);

  if (loading && !entries.length) {
    return (
      <div className="train-thread train-thread--empty" ref={threadRef}>
        <LoadingState title="Loading your last training session" inline />
      </div>
    );
  }

  if (!entries.length && !thinking) {
    return (
      <div className="train-thread train-thread--empty" ref={threadRef}>
        <span className="train-empty-icon">
          <Brain size={26} strokeWidth={1.7} />
        </span>
        <strong>
          {connected ? "Say something to begin" : "Not training yet"}
        </strong>
        <p>
          {connected
            ? "Tell it a fact about your business and it will remember."
            : "Start training to open a session with your assistant."}
        </p>
      </div>
    );
  }

  return (
    <div className="train-thread" ref={threadRef}>
      {entries.map((entry) => {
        if (entry.kind === "learned") {
          return (
            <div className="train-learned" key={entry.id}>
              <span className="train-learned-icon">
                <Check size={14} strokeWidth={3} />
              </span>
              <div>
                <span className="train-learned-label">Learned</span>
                {entry.question && (
                  <span className="train-learned-q">{entry.question}</span>
                )}
                <p>{entry.text}</p>
              </div>
            </div>
          );
        }

        const isUser = entry.kind === "user";

        return (
          <div
            className={`train-msg${isUser ? " is-user" : " is-assistant"}${entry.earlier ? " is-earlier" : ""}`}
            key={entry.id}
          >
            <span className="train-msg-avatar">
              {isUser ? <User size={15} strokeWidth={2} /> : assistantGlyph}
            </span>
            <div className="train-msg-body">
              <span className="train-msg-label">
                {isUser ? "You" : assistantName}
                <span>{entry.earlier ? "earlier" : entry.time}</span>
              </span>
              <p className="train-msg-bubble">{entry.text}</p>
            </div>
          </div>
        );
      })}

      {thinking && (
        <div className="train-msg is-assistant">
          <span className="train-msg-avatar">{assistantGlyph}</span>
          <div className="train-msg-body">
            <p className="train-msg-bubble train-typing">
              <span />
              <span />
              <span />
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
